/**
 * Sunrise/sunset calculation — pure math, no dependencies.
 *
 * Uses the standard solar-position algorithm (as popularised by the suncalc
 * library / NOAA): accurate to within a couple of minutes, which is plenty
 * for "when should I stop hiking" purposes.
 */

export interface SunTimes {
  sunrise: Date;
  sunset: Date;
}

const RAD = Math.PI / 180;
const DAY_MS = 1000 * 60 * 60 * 24;
const J1970 = 2440588;
const J2000 = 2451545;
const OBLIQUITY = RAD * 23.4397; // obliquity of the Earth's axis
const J0 = 0.0009;
// Standard sunrise/sunset altitude: sun's centre 0.833° below the horizon
// (accounts for refraction and the solar disc's radius)
const SUN_ALTITUDE = -0.833 * RAD;

function toJulian(date: Date): number {
  return date.valueOf() / DAY_MS - 0.5 + J1970;
}

function fromJulian(j: number): Date {
  return new Date((j + 0.5 - J1970) * DAY_MS);
}

function solarMeanAnomaly(d: number): number {
  return RAD * (357.5291 + 0.98560028 * d);
}

function eclipticLongitude(M: number): number {
  const C = RAD * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = RAD * 102.9372; // perihelion of the Earth
  return M + C + P + Math.PI;
}

function declination(eclipticLng: number): number {
  return Math.asin(Math.sin(OBLIQUITY) * Math.sin(eclipticLng));
}

/**
 * Sunrise and sunset for the calendar day containing `date`, at [lat, lng].
 * Returns null in polar day/night conditions where the sun never crosses
 * the horizon.
 */
export function getSunTimes(date: Date, lat: number, lng: number): SunTimes | null {
  const lw = RAD * -lng;
  const phi = RAD * lat;

  const d = toJulian(date) - J2000;
  const n = Math.round(d - J0 - lw / (2 * Math.PI));
  const ds = J0 + lw / (2 * Math.PI) + n; // approximate solar transit

  const M = solarMeanAnomaly(ds);
  const L = eclipticLongitude(M);
  const dec = declination(L);

  const Jnoon = J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);

  const cosH = (Math.sin(SUN_ALTITUDE) - Math.sin(phi) * Math.sin(dec)) /
    (Math.cos(phi) * Math.cos(dec));
  if (cosH < -1 || cosH > 1) return null; // polar day or polar night

  const w = Math.acos(cosH); // hour angle at the target altitude
  const Jset = J2000 + J0 + (w + lw) / (2 * Math.PI) + n + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
  const Jrise = Jnoon - (Jset - Jnoon);

  return {
    sunrise: fromJulian(Jrise),
    sunset: fromJulian(Jset),
  };
}
